import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface JobCancelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
  onCancelled: () => void;
}

export function JobCancelDialog({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  onCancelled,
}: JobCancelDialogProps) {
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for cancelling this job");
      return;
    }

    setIsLoading(true);
    try {
      // Get current user's profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) throw new Error("Profile not found");

      // Update job status to cancelled
      const { error: jobError } = await supabase
        .from("jobs")
        .update({ status: "cancelled" })
        .eq("id", jobId);

      if (jobError) throw jobError;

      // Insert cancellation record
      const { error: cancelError } = await supabase
        .from("job_cancellations")
        .insert({
          job_id: jobId,
          cancelled_by: profile.id,
          reason: reason.trim(),
        });

      if (cancelError) throw cancelError;

      // Archive the job for the current user via secure RPC (avoid RLS issues)
      const { error: archiveError } = await supabase.rpc('archive_job_for_current_user', {
        p_job_id: jobId,
        p_reason: `Job cancelled: ${reason.trim()}`,
      });

      if (archiveError) {
        console.warn("Failed to archive job:", archiveError);
      }

      // Notify all tradesmen who submitted quotes
      try {
        const { error: notifyError } = await supabase.functions.invoke('notify-job-cancellation', {
          body: {
            jobId: jobId,
            reason: reason.trim(),
          },
        });

        if (notifyError) {
          console.warn("Failed to send notifications:", notifyError);
        }
      } catch (notificationError) {
        console.warn("Notification service unavailable:", notificationError);
      }

      toast.success("Job cancelled successfully and notifications sent to quoted tradesmen");
      onCancelled();
      onClose();
      setReason("");
    } catch (error: any) {
      console.error("Error cancelling job:", error);
      if (error.message?.includes("Cannot cancel a job that has a completed payment")) {
        toast.error("Cannot cancel this job - payment has been completed. Only administrators can cancel paid jobs.");
      } else {
        toast.error("Failed to cancel job: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              You are about to cancel: <strong>{jobTitle}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              This action will permanently cancel the job. Please provide a reason:
            </p>
          </div>
          
          <div>
            <Label htmlFor="reason">Reason for cancellation</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Found another solution, Budget changed, No longer needed, etc."
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Keep Job
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel} 
              disabled={isLoading || !reason.trim()}
            >
              {isLoading ? "Cancelling..." : "Cancel Job"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}